import React, { useState, useEffect } from 'react';
import { BookOpen, GraduationCap, Calendar, ChevronsUp } from 'lucide-react';
import { TreeNode, TreeNodeRenderer } from './shared/TreeNode';
import { useTreeSelection } from './shared/useTreeSelection';
import { useTreeExpansion } from './shared/useTreeExpansion';
import { useTopicState } from './shared/useTopicState';
import { sortYears } from './shared/sortYears';
import { libraryYearDisplayName, librarySubjectBranchIdFromE179 } from '../utils/curriculumUtils';
import {
  canShowLibraryKeyStageSelectionCheckbox,
  canShowLibraryYearSelectionCheckbox,
  curriculumYearMayHaveTopics,
  isTopicInStudentCurriculumForSelectedStudent
} from '../utils/topicUtils';
import { getTopicInstructionalRollup, type InstructionalRollupKind } from '../utils/topicInstructionalUnits';
import { CurriculumLoadingState } from './CurriculumLoadingState';
import { useSessionDebugMode } from '../../../hooks/useSessionDebugMode';

interface CurriculumSubject {
  e179_Name: string;
  e179_SubjectId: string;
  /** Tenant custom catalog: with duplicate subject names, disambiguation uses name + education system. */
  e179_EducationSystemKey?: string;
  e179_KeyStages: CurriculumKeyStage[];
}

interface CurriculumKeyStage {
  e179_Name?: string;
  e179_KeyStageId: string;
  e179_Years: CurriculumYear[];
}

interface CurriculumYear {
  e179_Name?: string;
  e179_Number?: number;
  e179_YearId?: string;
  e179_Topics: CurriculumTopic[];
  e179_TotalTopicCount?: number | null;
}

interface CurriculumTopic {
  e179_Name?: string;
  e179_TopicId: string;
  e179_Details: string;
  e179_ChildTopics: CurriculumTopic[];
  e179_MinHours?: number;
  e179_PriorKnowledgeRequired?: string[];
  e179_NationalCurriculumContent?: string[];
  e179_Keywords?: Array<{ e179_Keyword: string; e179_Description?: string }>;
  e179_LearningObjectives?: string[];
  e179_SupervisionLevel?: string;
  e179_Outcome?: string;
  e179_TeacherTip?: string;
  e179_Misconceptions?: string;
  e179_DetailsLoaded?: boolean;
  e179_TotalChildTopicCount?: number | null;
}

interface CurriculumStructure {
  e179_Subjects: CurriculumSubject[];
}

interface CurriculumLibraryTreeViewProps {
  onNodeSelect?: (node: TreeNode) => void;
  selectedTopics?: any[];
  className?: string;
  expandedNodes?: Set<string>;
  onExpandedNodesChange?: (nodes: Set<string>) => void;
  curriculumData?: CurriculumStructure | null;
  studentCurriculumData?: any; // For checking if topics are already in the student curriculum
  isSelectingFromProvider?: boolean;
  units?: any[];
  passFailThreshold?: number | null;
  onLoadNodeChildren?: (nodeId: string, nodeType: 'year' | 'topic') => void;
  curriculumProviderKey?: string;
  loadedNodeIds?: Set<string>;
  loadingNodeIds?: Set<string>;
  showHint?: boolean; // Show hint message when no topics selected (for mobile/tablet)
  /** When true, year-level topics are treated as already loaded (tenant custom catalog is fully hydrated in one event). */
  assumeTopicsLoaded?: boolean;
  /** Used in the "Select Topics…" hint (e.g. "Sam's Curriculum"). */
  selectedStudentFirstName?: string;
  /** When set, E180-based "already in plan" checks only apply if E180 matches this student. */
  selectedStudentIdForStudentCurriculumCheck?: string | null;
  /** When set with units, topic rows surface instructional unit drill-down consistent with student tree. */
  onOpenInstructionalUnit?: (unit: any) => void;
}

const viewStates = {
  loading: 0,
  error: 1,
  empty: 2,
  noData: 3,
  ready: 4
};

function formatFrameworkLabelForTree(esKey: string): string {
  const k = esKey.trim();
  if (!k) return '';
  if (k === 'uk-national-curriculum') return 'UK National Curriculum';
  if (k.toLowerCase().startsWith('tenant-custom:')) return 'Custom framework';
  return k;
}

function sourceEducationSystemKeyForLibrarySubject(
  subject: CurriculumSubject,
  curriculumProviderKey?: string
): string | null {
  const fromSub = (subject.e179_EducationSystemKey || '').trim();
  if (fromSub) return fromSub;
  const pk = (curriculumProviderKey || '').trim().toLowerCase();
  if (pk === 'oak') return 'uk-national-curriculum';
  return pk || null;
}

function normalizeSubjectNameKey(name: string | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

function effectiveEducationSystemKey(subject: CurriculumSubject): string {
  return (subject.e179_EducationSystemKey ?? '').trim();
}

/** Subject names (case-insensitive) that appear under more than one education system in this catalog. */
function buildSubjectNamesWithMultipleFrameworks(subjects: CurriculumSubject[]): Set<string> {
  const byName = new Map<string, Set<string>>();
  for (const s of subjects) {
    const nameKey = normalizeSubjectNameKey(s.e179_Name);
    if (!nameKey) continue;
    const es = effectiveEducationSystemKey(s);
    let esSet = byName.get(nameKey);
    if (!esSet) {
      esSet = new Set<string>();
      byName.set(nameKey, esSet);
    }
    esSet.add(es);
  }
  const out = new Set<string>();
  for (const [nameKey, esSet] of byName) {
    if (esSet.size > 1) out.add(nameKey);
  }
  return out;
}

function subjectTreeLabel(
  subject: CurriculumSubject,
  opts: { isTenantCustomCurriculum: boolean; subjectNamesWithMultipleFrameworks: Set<string> }
): string {
  const name = subject.e179_Name || '';
  if (!opts.isTenantCustomCurriculum) return name;
  const nameKey = normalizeSubjectNameKey(name);
  if (!nameKey || !opts.subjectNamesWithMultipleFrameworks.has(nameKey)) return name;
  const es = effectiveEducationSystemKey(subject);
  return `${name} — ${formatFrameworkLabelForTree(es)}`;
}

export function CurriculumLibraryTreeView({
  onNodeSelect,
  selectedTopics = [],
  className = '',
  expandedNodes,
  onExpandedNodesChange,
  curriculumData,
  studentCurriculumData,
  isSelectingFromProvider = false,
  units = [],
  passFailThreshold = null,
  onLoadNodeChildren,
  curriculumProviderKey,
  loadedNodeIds = new Set(),
  loadingNodeIds = new Set(),
  showHint = false,
  assumeTopicsLoaded = false,
  selectedStudentFirstName = '',
  selectedStudentIdForStudentCurriculumCheck = null,
  onOpenInstructionalUnit
}: CurriculumLibraryTreeViewProps) {
  const sessionDebugMode = useSessionDebugMode();
  const studentCurriculumLabel =
    selectedStudentFirstName.trim() !== ''
      ? `${selectedStudentFirstName.trim()}'s Curriculum`
      : 'Curriculum';
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [viewState, setViewState] = useState<number>(!curriculumData ? viewStates.loading : viewStates.ready);
  const [nodesToAutoSelect, setNodesToAutoSelect] = useState<Set<string>>(new Set());

  const emptyCurriculumMessage = 'Please choose a Provider to add curriculum topics';
  const noCurriculumDataMessage = 'No curriculum data available';
  const collapseAllTitle = 'Collapse All';

  // Update viewState based on curriculumData
  useEffect(() => {
    if (!curriculumData) {
      setViewState(viewStates.loading);
    } else if (!curriculumData.e179_Subjects || curriculumData.e179_Subjects.length === 0) {
      setViewState(viewStates.noData);
    } else {
      setViewState(viewStates.ready);
    }
  }, [curriculumData]);

  // Check if topic is disabled (already in student curriculum)
  // Provider library: backend sets e179_IsInStudentCurriculum when loading nodes.
  // Tenant custom catalog often omits that flag; also match the selection guard via studentCurriculumData.
  const isTopicDisabled = (topicData: any): boolean => {
    if (!topicData) return false;
    if (topicData.e179_IsInStudentCurriculum === true) return true;
    const topicId = topicData.e179_TopicId || topicData.e180_TopicId;
    if (topicId && studentCurriculumData) {
      return isTopicInStudentCurriculumForSelectedStudent(
        String(topicId),
        studentCurriculumData,
        selectedStudentIdForStudentCurriculumCheck
      );
    }
    return false;
  };

  // Use shared hooks
  const {
    isTopicSelected,
    getLeafTopicNodes,
    getCheckboxState,
    hasDisabledDescendants,
    isNodeCheckboxDisabled
  } = useTreeSelection({
    selectedTopics,
    isSelecting: isSelectingFromProvider,
    isTopicDisabled
  });

  const { toggleNode, handleCollapseAll, nodesToAutoSelect: autoSelectNodes, setNodesToAutoSelect: setAutoSelectNodes } = useTreeExpansion({
    expandedNodes,
    onExpandedNodesChange,
    onLoadNodeChildren,
    loadedNodeIds,
    loadingNodeIds,
    canLoadChildren: !!curriculumProviderKey
  });

  const { getAssessmentScoreForTopic } = useTopicState({
    units,
    isStudentCurriculum: false
  });

  // Auto-select children when they become available
  useEffect(() => {
    if (!isSelectingFromProvider || autoSelectNodes.size === 0) return;
    
    const treeNodes = buildTreeNodes();
    const findNodeById = (nodes: TreeNode[], id: string): TreeNode | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = findNodeById(node.children, id);
          if (found) return found;
        }
      }
      return null;
    };
    
    const nodesToProcess = Array.from(autoSelectNodes);
    nodesToProcess.forEach(nodeId => {
      if (loadedNodeIds.has(nodeId) && !loadingNodeIds.has(nodeId)) {
        const node = findNodeById(treeNodes, nodeId);
        if (node) {
          const leafNodes = getLeafTopicNodes(node);
          const selectableLeafTopics = leafNodes
            .filter(leafNode => leafNode.data && !isTopicDisabled(leafNode.data))
            .map(leafNode => leafNode.data);
          
          selectableLeafTopics.forEach(topicData => {
            if (topicData && !isTopicSelected(topicData)) {
              const topicNode: TreeNode = {
                id: `${nodeId}-leaf-${topicData.e179_TopicId}`,
                label: topicData.e179_Name || '',
                type: 'topic',
                data: topicData
              };
              onNodeSelect?.(topicNode);
            }
          });
          
          setAutoSelectNodes(prev => {
            const newSet = new Set(prev);
            newSet.delete(nodeId);
            return newSet;
          });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedNodeIds, loadingNodeIds, curriculumData]);

  const selectNode = (node: TreeNode) => {
    if (node.type === 'topic' && node.data && isTopicDisabled(node.data)) {
      return;
    }
    
    setSelectedNode(node.id);
    onNodeSelect?.(node);
  };

  const handleCheckboxClick = (node: TreeNode, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isSelectingFromProvider) return;
    if (isNodeCheckboxDisabled(node)) return;

    if (node.type === 'topic' && node.data) {
      const hasChildTopics = node.data.e179_ChildTopics && node.data.e179_ChildTopics.length > 0;
      if (!hasChildTopics) {
        onNodeSelect?.(node);
        return;
      }
    }
    
    const isExpanded = expandedNodes ? expandedNodes.has(node.id) : false;
    const currentState = getCheckboxState(node);
    
    if (!isExpanded && currentState === 'unchecked') {
      if (onLoadNodeChildren && curriculumProviderKey && !loadedNodeIds.has(node.id) && !loadingNodeIds.has(node.id)) {
        const mayLazyYear =
          node.type === 'year' && node.data && curriculumYearMayHaveTopics(node.data);
        if (mayLazyYear) {
          toggleNode(node.id, 'year');
          setAutoSelectNodes(prev => new Set(prev).add(node.id));
          return;
        }
      }
    }
    
    const leafNodes = getLeafTopicNodes(node);
    const shouldSelect = currentState !== 'checked';
    const selectableLeafTopics = leafNodes
      .filter(leafNode => leafNode.data && !isTopicDisabled(leafNode.data))
      .map(leafNode => leafNode.data);
    
    if (shouldSelect) {
      selectableLeafTopics.forEach(topicData => {
        if (topicData && !isTopicSelected(topicData)) {
          const topicNode: TreeNode = {
            id: `${node.id}-leaf-${topicData.e179_TopicId}`,
            label: topicData.e179_Name || '',
            type: 'topic',
            data: topicData
          };
          onNodeSelect?.(topicNode);
        }
      });
    } else {
      selectableLeafTopics.forEach(topicData => {
        if (topicData && isTopicSelected(topicData)) {
          const topicNode: TreeNode = {
            id: `${node.id}-leaf-${topicData.e179_TopicId}`,
            label: topicData.e179_Name || '',
            type: 'topic',
            data: topicData
          };
          onNodeSelect?.(topicNode);
        }
      });
    }
  };

  const buildTreeNodes = (): TreeNode[] => {
    if (!curriculumData) return [];

    const isTenantCustomCurriculum = curriculumProviderKey === 'custom';
    const catalogSubjects =
      isTenantCustomCurriculum || sessionDebugMode
        ? curriculumData.e179_Subjects
        : curriculumData.e179_Subjects.filter(
            (s) => (s.e179_Name || '').trim().toLowerCase() !== 'cooking and nutrition'
          );
    const subjectNamesWithMultipleFrameworks = isTenantCustomCurriculum
      ? buildSubjectNamesWithMultipleFrameworks(catalogSubjects)
      : new Set<string>();

    // Sort subjects alphanumerically
    const sortedSubjects = [...catalogSubjects].sort((a, b) => 
      (a.e179_Name || '').localeCompare(b.e179_Name || '')
    );

    return sortedSubjects.map(subject => {
      const branchId = librarySubjectBranchIdFromE179(subject);
      const catalogSubjectId =
        subject.e179_SubjectId != null && String(subject.e179_SubjectId).trim() !== ''
          ? String(subject.e179_SubjectId)
          : '';
      const displayName = subject.e179_Name || '';
      const educationSystemKey = sourceEducationSystemKeyForLibrarySubject(subject, curriculumProviderKey);
      // Sort key stages alphanumerically
      const sortedKeyStages = [...subject.e179_KeyStages].sort((a, b) => 
        (a.e179_Name || '').localeCompare(b.e179_Name || '')
      );
      
      return {
        id: branchId,
        label: subjectTreeLabel(subject, { isTenantCustomCurriculum, subjectNamesWithMultipleFrameworks }),
        type: 'subject' as const,
        icon: BookOpen,
        children: sortedKeyStages.map((keystage) => {
          // Find original index for node ID
          const originalKeyStageIndex = subject.e179_KeyStages.indexOf(keystage);
          return {
          id: `${branchId}-ks-${originalKeyStageIndex}`,
          label: keystage.e179_Name || `Key Stage ${originalKeyStageIndex + 1}`,
          type: 'keystage' as const,
          icon: GraduationCap,
          children: sortYears(keystage.e179_Years, false).map((year, yearIndex) => {
            const originalYearIndex = keystage.e179_Years.indexOf(year);
            const yearNodeId = `${branchId}-y-${originalKeyStageIndex}-${originalYearIndex}`;
            const topicsLoaded = assumeTopicsLoaded || loadedNodeIds.has(yearNodeId);
            const topics = topicsLoaded && year.e179_Topics ? year.e179_Topics : [];
            const yearDisplay =
              (year.e179_Name || '').trim() ||
              libraryYearDisplayName(year) ||
              `Year ${year.e179_Number ?? originalYearIndex + 1}`;
            const yearIdStr =
              year.e179_YearId != null && String(year.e179_YearId).trim() !== ''
                ? String(year.e179_YearId)
                : '';
            const ksIdStr =
              keystage.e179_KeyStageId != null && String(keystage.e179_KeyStageId).trim() !== ''
                ? String(keystage.e179_KeyStageId)
                : '';

            return {
              id: yearNodeId,
              label: yearDisplay,
              type: 'year' as const,
              icon: Calendar,
              data: {
                e179_TotalTopicCount: year.e179_TotalTopicCount
              },
              children: topics.length > 0
                ? buildTopicNodes(
                    displayName,
                    branchId,
                    catalogSubjectId,
                    keystage.e179_Name,
                    ksIdStr || null,
                    yearIdStr || null,
                    yearDisplay,
                    educationSystemKey,
                    originalKeyStageIndex,
                    originalYearIndex,
                    topics
                  )
                : undefined
            };
          })
        };
        })
      };
    });
  };

  const buildTopicNodes = (
    subjectDisplayName: string,
    subjectBranchId: string,
    catalogSubjectId: string,
    keyStageName: string | undefined,
    keyStageId: string | null,
    yearId: string | null,
    yearDisplayLabel: string,
    educationSystemKey: string | null,
    keystageIndex: number,
    yearIndex: number,
    topics: any[]
  ): TreeNode[] => {
    return topics.map((topic, topicIndex) => {
      const topicWithMetadata = {
        ...topic,
        _metadata: {
          subjectName: subjectDisplayName,
          subjectId: catalogSubjectId || null,
          keyStageName,
          keyStageId,
          yearId,
          yearLabel: yearDisplayLabel,
          educationSystemKey
        }
      };
      return {
        id: `${subjectBranchId}-${keystageIndex}-${yearIndex}-${topicIndex}`,
        label: topic.e179_Name || `Topic ${topicIndex + 1}`,
        type: 'topic' as const,
        data: topicWithMetadata,
        children: topic.e179_ChildTopics && topic.e179_ChildTopics.length > 0 
          ? buildChildTopicNodes(
              subjectDisplayName,
              subjectBranchId,
              catalogSubjectId,
              keyStageName,
              keyStageId,
              yearId,
              yearDisplayLabel,
              educationSystemKey,
              keystageIndex,
              yearIndex,
              topicIndex,
              topic.e179_ChildTopics
            )
          : undefined
      };
    });
  };

  const buildChildTopicNodes = (
    subjectDisplayName: string,
    subjectBranchId: string,
    catalogSubjectId: string,
    keyStageName: string | undefined,
    keyStageId: string | null,
    yearId: string | null,
    yearDisplayLabel: string,
    educationSystemKey: string | null,
    keystageIndex: number,
    yearIndex: number,
    parentTopicIndex: number | string,
    childTopics: any[]
  ): TreeNode[] => {
    return childTopics.map((childTopic, childIndex) => {
      const childTopicWithMetadata = {
        ...childTopic,
        _metadata: {
          subjectName: subjectDisplayName,
          subjectId: catalogSubjectId || null,
          keyStageName,
          keyStageId,
          yearId,
          yearLabel: yearDisplayLabel,
          educationSystemKey
        }
      };
      const childTopicNodeId = `${subjectBranchId}-${keystageIndex}-${yearIndex}-${parentTopicIndex}-${childIndex}`;
      const grandChildTopics = childTopic.e179_ChildTopics ?? [];

      return {
        id: childTopicNodeId,
        label: childTopic.e179_Name || `Sub-topic ${childIndex + 1}`,
        type: 'topic' as const,
        data: childTopicWithMetadata,
        children: grandChildTopics.length > 0
          ? buildChildTopicNodes(
              subjectDisplayName,
              subjectBranchId,
              catalogSubjectId,
              keyStageName,
              keyStageId,
              yearId,
              yearDisplayLabel,
              educationSystemKey,
              keystageIndex,
              yearIndex,
              `${parentTopicIndex}-${childIndex}`,
              grandChildTopics
            )
          : undefined
      };
    });
  };

  const renderTreeNode = (node: TreeNode, depth: number = 0): React.ReactNode => {
    const isExpanded = expandedNodes ? expandedNodes.has(node.id) : false;
    const isSelected = selectedNode === node.id;
    const hasChildren = node.children && node.children.length > 0;
    const isLoading = loadingNodeIds.has(node.id);
    const yearLazyExpand =
      node.type === 'year' &&
      Boolean(node.data) &&
      onLoadNodeChildren &&
      Boolean(curriculumProviderKey) &&
      !loadedNodeIds.has(node.id) &&
      curriculumYearMayHaveTopics(node.data);
    const canHaveChildren = yearLazyExpand;
    const showExpandIcon = hasChildren || canHaveChildren;
    
    const isTopicSelectedInCards = node.type === 'topic' && node.data ? isTopicSelected(node.data) : false;
    const isDisabled = node.type === 'topic' && node.data ? isTopicDisabled(node.data) : false;
    const catalogTopicLeaf =
      node.type === 'topic' &&
      Boolean(node.data) &&
      (!node.children || node.children.length === 0);

    const catalogTopicIdForLeaf = catalogTopicLeaf && node.data ? node.data.e179_TopicId : null;

    let instructionalRollupLib: InstructionalRollupKind | undefined;
    let instructionalUnitsLib: any[] = [];
    if (catalogTopicLeaf && catalogTopicIdForLeaf) {
      const rolled = getTopicInstructionalRollup(units, catalogTopicIdForLeaf);
      instructionalUnitsLib = rolled.qualifyingUnits;
      instructionalRollupLib = rolled.rollup;
    }

    const assessmentScoreRollup =
      catalogTopicLeaf && instructionalRollupLib === 'allComplete' && catalogTopicIdForLeaf
        ? getAssessmentScoreForTopic(catalogTopicIdForLeaf)
        : null;
    
    const showCheckbox =
      isSelectingFromProvider &&
      (node.type === 'topic' ||
        (node.type === 'year' &&
          canShowLibraryYearSelectionCheckbox(
            node.id,
            node.data,
            loadedNodeIds,
            assumeTopicsLoaded
          )) ||
        (node.type === 'keystage' &&
          canShowLibraryKeyStageSelectionCheckbox(
            node.children ?? [],
            loadedNodeIds,
            assumeTopicsLoaded
          )));
    const checkboxState = showCheckbox ? getCheckboxState(node) : 'unchecked';
    const checkboxDisabled = showCheckbox && isNodeCheckboxDisabled(node);
    const isChecked =
      checkboxState === 'checked' ||
      (checkboxDisabled && checkboxState !== 'indeterminate');
    const isIndeterminate = !checkboxDisabled && checkboxState === 'indeterminate';
    const hasDisabledDesc = showCheckbox && hasDisabledDescendants(node);

    const hasChildTopics = node.type === 'topic' && node.data && node.data.e179_ChildTopics && node.data.e179_ChildTopics.length > 0;
    const childTopicsCount = node.type === 'topic' && node.data ? (node.data.e179_TotalChildTopicCount ?? (node.data.e179_ChildTopics?.length || 0)) : 0;
    const yearTopicsCount = node.type === 'year' && node.data ? node.data.e179_TotalTopicCount : undefined;

    return (
      <div key={node.id}>
        <TreeNodeRenderer
          node={node}
          depth={depth}
          isExpanded={isExpanded}
          isLoading={isLoading}
          showExpandIcon={showExpandIcon}
          showCheckbox={showCheckbox}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          checkboxDisabled={checkboxDisabled}
          isDisabled={isDisabled}
          isTopicSelectedInCards={isTopicSelectedInCards}
          topicLeafInstructionalRollup={catalogTopicLeaf ? instructionalRollupLib : undefined}
          instructionalUnits={catalogTopicLeaf ? instructionalUnitsLib : undefined}
          onOpenInstructionalUnit={onOpenInstructionalUnit}
          assessmentScore={assessmentScoreRollup}
          passFailThreshold={passFailThreshold}
          hasChildTopics={hasChildTopics}
          childTopicsCount={childTopicsCount}
          yearTopicsCount={yearTopicsCount}
          onToggleExpand={() => {
            const isParentTopic =
              node.type === 'topic' &&
              node.data &&
              node.data.e179_ChildTopics &&
              node.data.e179_ChildTopics.length > 0;

            if (isParentTopic) {
              toggleNode(node.id, node.type);
            } else {
              if (
                hasChildren ||
                (node.type === 'year' && (yearLazyExpand || isExpanded)) ||
                (node.type === 'topic' && (isExpanded || hasChildTopics))
              ) {
                toggleNode(node.id, node.type);
              }
              if (node.type === 'topic' && node.data) {
                selectNode(node);
              }
            }
          }}
          onCheckboxClick={(e) => handleCheckboxClick(node, e)}
          onNodeClick={() => {
            if (isDisabled) return;
            const isParentTopic =
              node.type === 'topic' &&
              node.data &&
              node.data.e179_ChildTopics &&
              node.data.e179_ChildTopics.length > 0;

            if (isParentTopic) {
              toggleNode(node.id, node.type);
            } else {
              if (
                hasChildren ||
                (node.type === 'year' && (yearLazyExpand || isExpanded)) ||
                (node.type === 'topic' && (isExpanded || hasChildTopics))
              ) {
                toggleNode(node.id, node.type);
              }
              if (node.type === 'topic' && node.data) {
                selectNode(node);
              }
            }
          }}
        />
        
        {isExpanded && hasChildren && (
          <div>
            {node.children!.map(child => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const treeNodes = buildTreeNodes();

  switch (viewState) {
    case viewStates.loading:
      return (
        <div className={`p-4 ${className}`}>
          <CurriculumLoadingState title="Loading National Curriculum" />
        </div>
      );

    case viewStates.error:
      return (
        <div className={`p-4 ${className}`}>
          <div className="text-center py-8">
            <div className="text-destructive-action mb-2">Error loading curriculum</div>
          </div>
        </div>
      );

    case viewStates.noData:
      return (
        <div className={`p-4 ${className}`}>
          <div className="text-center py-8">
            <div className="text-gray-500">{noCurriculumDataMessage}</div>
          </div>
        </div>
      );

    case viewStates.ready: {
      // Tenant custom catalog sits above the national tree; size to content when compact.
      const compact = assumeTopicsLoaded;
      return (
        <div className={`${className} flex flex-col ${compact ? 'h-auto min-h-0' : 'h-full'}`}>
          <div
            className={
              compact
                ? 'overflow-x-hidden pb-2 relative'
                : 'flex-1 overflow-auto relative'
            }
          >
            {showHint && (
              <div className="pl-2 pr-2 lg:pr-4 pb-2 pt-2">
                <p className="text-base text-gray-600">
                  <span className="font-medium">Select Topics</span> to add them to {studentCurriculumLabel}
                </p>
              </div>
            )}
            <button
              onClick={handleCollapseAll}
              className="absolute top-2 right-2 lg:right-4 z-10 text-gray-600 hover:text-gray-900 transition-colors p-1 rounded hover:bg-gray-100 bg-white shadow-sm"
              title={collapseAllTitle}
            >
              <ChevronsUp className="w-4 h-4" />
            </button>

            <div className="pt-2">
              {treeNodes.map(node => renderTreeNode(node))}
            </div>
          </div>
        </div>
      );
    }

    default:
      return null;
  }
}
